/**
 * Keyboard Shortcuts catalog — SINGLE SOURCE OF TRUTH for the Keyboard Shortcuts modal.
 *
 * ⚠️ ANTI-STALENESS CONTRACT (read before adding/changing any shortcut anywhere in apps/console):
 *   Every binding here has a stable `id`. Each `id` MUST have at least one matching
 *   `SHORTCUT_ID: <id>` marker comment co-located with the code that actually implements the
 *   binding (a keydown branch, a ReactFlow key prop, a mouse-gesture handler). The reverse also
 *   holds: every `SHORTCUT_ID: <id>` marker in the tree MUST correspond to an `id` in this file.
 *   `__tests__/keyboardShortcutsCoverage.test.ts` enforces this in BOTH directions and fails CI
 *   on drift — so when you add, remove, or change a keyboard/mouse control:
 *     1. update (or add) the binding here, and
 *     2. update (or add) the `SHORTCUT_ID: <id>` marker next to the implementing code.
 *   See docs/canvas-specs/19-keyboard-shortcuts.md for the full convention.
 *
 * Scope of this catalog: canvas-global controls + the in-node editor surfaces the user actually
 * discovers (Timeline / Video editor, Audio Remix editor). Text-field submit keys (Enter /
 * Shift+Enter / Cmd+Enter inside prompt/chat inputs) are intentionally out of scope.
 */

/** A platform-aware modifier token, or a literal key / mouse-gesture label. */
export type KeyToken =
  | { mod: 'mod' | 'shift' | 'alt' }
  | { key: string }

/** One key combination (rendered as a run of <kbd> chips joined by "+"). */
export type Combo = KeyToken[]

export interface ShortcutBinding {
  /** Stable id — must match a `SHORTCUT_ID: <id>` marker in the implementing code. */
  id: string
  /** One or more equivalent combos (e.g. Delete OR Backspace). Rendered joined by "or". */
  combos: Combo[]
  /** i18n key under the `canvas` namespace for the human-readable action label. */
  labelKey: string
}

export type ShortcutScope = 'canvas' | 'timeline' | 'audio' | 'annotate' | 'shotList'

export interface ShortcutGroup {
  scope: ShortcutScope
  /** i18n key under the `canvas` namespace for the group heading. */
  titleKey: string
  bindings: ShortcutBinding[]
}

const mod: KeyToken = { mod: 'mod' }
const shift: KeyToken = { mod: 'shift' }
const k = (key: string): KeyToken => ({ key })

/**
 * True on macOS-family platforms (render ⌘/⌥/⇧); false elsewhere (render Ctrl/Alt/Shift).
 * Guarded for SSR / non-browser (tests) — defaults to non-mac.
 */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  const p = `${navigator.platform || ''} ${navigator.userAgent || ''}`
  return /Mac|iPhone|iPad|iPod/i.test(p)
}

/** Resolve one combo to display chip strings, platform-aware. */
export function resolveCombo(combo: Combo, isMac: boolean): string[] {
  return combo.map((tok) => {
    if ('mod' in tok) {
      if (tok.mod === 'mod') return isMac ? '⌘' : 'Ctrl'
      if (tok.mod === 'shift') return isMac ? '⇧' : 'Shift'
      return isMac ? '⌥' : 'Alt'
    }
    return tok.key
  })
}

/**
 * The catalog. Keep grouped by scope. Group order = display order.
 * IDs are namespaced by scope (canvas.* / timeline.* / audio.*).
 */
export const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    scope: 'canvas',
    titleKey: 'keyboardShortcuts.groups.canvas',
    bindings: [
      { id: 'canvas.copy', combos: [[mod, k('C')]], labelKey: 'keyboardShortcuts.canvas.copy' },
      { id: 'canvas.paste', combos: [[mod, k('V')]], labelKey: 'keyboardShortcuts.canvas.paste' },
      { id: 'canvas.duplicate', combos: [[mod, k('D')]], labelKey: 'keyboardShortcuts.canvas.duplicate' },
      { id: 'canvas.delete', combos: [[k('Delete')], [k('Backspace')]], labelKey: 'keyboardShortcuts.canvas.delete' },
      { id: 'canvas.undo', combos: [[mod, k('Z')]], labelKey: 'keyboardShortcuts.canvas.undo' },
      { id: 'canvas.redo', combos: [[mod, shift, k('Z')]], labelKey: 'keyboardShortcuts.canvas.redo' },
      { id: 'canvas.cycleNext', combos: [[k('Tab')]], labelKey: 'keyboardShortcuts.canvas.cycleNext' },
      { id: 'canvas.cyclePrev', combos: [[shift, k('Tab')]], labelKey: 'keyboardShortcuts.canvas.cyclePrev' },
      { id: 'canvas.zoomInDoubleClick', combos: [[k('Double-click')]], labelKey: 'keyboardShortcuts.canvas.zoomInDoubleClick' },
      { id: 'canvas.zoomOutDoubleClick', combos: [[shift, k('Double-click')]], labelKey: 'keyboardShortcuts.canvas.zoomOutDoubleClick' },
      {
        id: 'canvas.zoomWheel',
        combos: [[mod, k('Scroll')]],
        labelKey: 'keyboardShortcuts.canvas.zoomWheel',
      },
      { id: 'canvas.zoomPinch', combos: [[k('Pinch')]], labelKey: 'keyboardShortcuts.canvas.zoomPinch' },
      { id: 'canvas.pan', combos: [[k('Scroll')], [k('Drag')]], labelKey: 'keyboardShortcuts.canvas.pan' },
      { id: 'canvas.boxSelect', combos: [[shift, k('Drag')]], labelKey: 'keyboardShortcuts.canvas.boxSelect' },
      { id: 'canvas.multiSelect', combos: [[mod, k('Click')]], labelKey: 'keyboardShortcuts.canvas.multiSelect' },
      { id: 'canvas.contextMenu', combos: [[k('Right-click')]], labelKey: 'keyboardShortcuts.canvas.contextMenu' },
      { id: 'canvas.closeModal', combos: [[k('Esc')]], labelKey: 'keyboardShortcuts.canvas.closeModal' },
    ],
  },
  {
    scope: 'timeline',
    titleKey: 'keyboardShortcuts.groups.timeline',
    bindings: [
      { id: 'timeline.playPause', combos: [[k('Space')]], labelKey: 'keyboardShortcuts.timeline.playPause' },
      { id: 'timeline.split', combos: [[k('S')]], labelKey: 'keyboardShortcuts.timeline.split' },
      { id: 'timeline.delete', combos: [[k('Delete')], [k('Backspace')]], labelKey: 'keyboardShortcuts.timeline.delete' },
      { id: 'timeline.rippleDelete', combos: [[shift, k('Delete')], [shift, k('Backspace')]], labelKey: 'keyboardShortcuts.timeline.rippleDelete' },
      { id: 'timeline.undo', combos: [[mod, k('Z')]], labelKey: 'keyboardShortcuts.timeline.undo' },
      { id: 'timeline.redo', combos: [[mod, shift, k('Z')]], labelKey: 'keyboardShortcuts.timeline.redo' },
      { id: 'timeline.copy', combos: [[mod, k('C')]], labelKey: 'keyboardShortcuts.timeline.copy' },
      { id: 'timeline.paste', combos: [[mod, k('V')]], labelKey: 'keyboardShortcuts.timeline.paste' },
      { id: 'timeline.fullscreen', combos: [[k('F')]], labelKey: 'keyboardShortcuts.timeline.fullscreen' },
      { id: 'timeline.shot', combos: [[k('↑')], [k('↓')]], labelKey: 'keyboardShortcuts.timeline.shot' },
      { id: 'timeline.wheelZoom', combos: [[mod, k('Wheel')]], labelKey: 'keyboardShortcuts.timeline.wheelZoom' },
      { id: 'timeline.wheelScroll', combos: [[k('Wheel')]], labelKey: 'keyboardShortcuts.timeline.wheelScroll' },
      { id: 'timeline.scrub', combos: [[k('←')], [k('→')]], labelKey: 'keyboardShortcuts.timeline.scrub' },
      { id: 'timeline.fineScrub', combos: [[shift, k('←')], [shift, k('→')]], labelKey: 'keyboardShortcuts.timeline.fineScrub' },
      { id: 'timeline.jumpStart', combos: [[k('Home')]], labelKey: 'keyboardShortcuts.timeline.jumpStart' },
      { id: 'timeline.jumpEnd', combos: [[k('End')]], labelKey: 'keyboardShortcuts.timeline.jumpEnd' },
      { id: 'timeline.zoomIn', combos: [[k('+')], [k('=')]], labelKey: 'keyboardShortcuts.timeline.zoomIn' },
      { id: 'timeline.zoomOut', combos: [[k('−')]], labelKey: 'keyboardShortcuts.timeline.zoomOut' },
      { id: 'timeline.multiSelectClip', combos: [[shift, k('Click')]], labelKey: 'keyboardShortcuts.timeline.multiSelectClip' },
    ],
  },
  {
    scope: 'shotList',
    titleKey: 'keyboardShortcuts.groups.shotList',
    bindings: [
      { id: 'shotList.undo', combos: [[mod, k('Z')]], labelKey: 'keyboardShortcuts.shotList.undo' },
      { id: 'shotList.redo', combos: [[mod, shift, k('Z')]], labelKey: 'keyboardShortcuts.shotList.redo' },
      { id: 'shotList.commitCell', combos: [[k('Enter')], [mod, k('Enter')]], labelKey: 'keyboardShortcuts.shotList.commitCell' },
      { id: 'shotList.cancelCell', combos: [[k('Esc')]], labelKey: 'keyboardShortcuts.shotList.cancelCell' },
    ],
  },
  {
    // RFC-1115 image annotation editor. These are live only while the editor is
    // open, which is also when `useBlockingOverlay` stands the canvas handlers
    // down — so annotate.deleteElement removes the selected shape rather than
    // the node underneath it.
    scope: 'annotate',
    titleKey: 'keyboardShortcuts.groups.annotate',
    bindings: [
      { id: 'annotate.undo', combos: [[mod, k('Z')]], labelKey: 'keyboardShortcuts.annotate.undo' },
      { id: 'annotate.redo', combos: [[mod, shift, k('Z')]], labelKey: 'keyboardShortcuts.annotate.redo' },
      { id: 'annotate.deleteElement', combos: [[k('Delete')], [k('Backspace')]], labelKey: 'keyboardShortcuts.annotate.deleteElement' },
      { id: 'annotate.moveElement', combos: [[k('Drag')]], labelKey: 'keyboardShortcuts.annotate.moveElement' },
    ],
  },
]

/** Flat list of all binding ids — used by the coverage test. */
export const ALL_SHORTCUT_IDS: string[] = SHORTCUT_GROUPS.flatMap((g) => g.bindings.map((b) => b.id))
